/**
 * Your own agent keys: the only kind remote control takes. A key an administrator issued for you
 * reports your agents' telemetry, but cannot receive what you send your agents — so whoever issued
 * it cannot read or intercept your instructions. Issue one here, set it on your machine, done.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, ShieldAlert } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDate, formatRelative } from '../lib/format'
import { CodeBlock } from './primitives'
import { guessSystem, storeKeyCommand } from '../lib/keySetup'
import { KeyCheck } from './KeyCheck'

/** `ownKeys`: how many the server counts; left out, the keys listed here are counted. */
export function MyAgentKeys({ ownKeys }: { ownKeys?: number }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [issued, setIssued] = useState<{ key: string; prefix: string } | null>(null)
  const [machine, setMachine] = useState('')

  const { data: keys } = useQuery({ queryKey: ['me', 'credentials'], queryFn: api.me.credentials })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'credentials'] })
    void queryClient.invalidateQueries({ queryKey: ['remote'] })
  }

  const issue = useMutation({
    mutationFn: () => api.me.issueCredential({ name: machine.trim() || undefined, machineLabel: machine.trim() || undefined }),
    onSuccess: (result) => {
      setIssued({ key: result.key, prefix: result.keyPrefix })
      setMachine('')
      refresh()
    },
  })
  const revoke = useMutation({ mutationFn: (id: string) => api.me.revokeCredential(id), onSuccess: refresh })

  const failure = issue.error ?? revoke.error
  const active = (keys ?? []).filter((k) => !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt) > new Date()))

  return (
    <div className="stack stack--tight my-keys">
      <span className="field__label">{t('remote.keys.title')}</span>
      <span className="tiny muted">{t('remote.keys.why')}</span>

      {(ownKeys ?? active.length) === 0 && !issued && (
        <div className="my-keys__warning small" role="note">
          <ShieldAlert size={14} aria-hidden /> {t('remote.keys.none')}
        </div>
      )}

      {issued && (
        <div className="stack stack--tight my-keys__issued" role="status">
          <span className="small bold">{t('remote.keys.issued')}</span>
          <CodeBlock code={storeKeyCommand(guessSystem(), issued.key)} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          <span className="tiny muted">{t('remote.keys.issuedHint')}</span>
        </div>
      )}

      {active.length > 0 && (
        <ul className="my-keys__list">
          {active.map((k) => (
            <li key={k.id} className="row row--tight">
              <KeyRound size={13} aria-hidden className="muted" />
              <span className="mono small">{k.keyPrefix}</span>
              <span className="small truncate">{k.machineLabel ?? k.name}</span>
              <span className={`tiny ${k.issuedByMe ? 'my-keys__ok' : 'muted'}`}>{k.issuedByMe ? t('remote.keys.remoteOk') : t('remote.keys.telemetryOnly')}</span>
              <div className="spacer" />
              <span className="tiny muted">
                {k.lastUsedAt ? t('remote.keys.used', { when: formatRelative(k.lastUsedAt, locale) }) : t('remote.keys.neverUsed')}
                {k.expiresAt ? ` · ${t('remote.keys.expires', { date: formatDate(k.expiresAt, locale) })}` : ''}
              </span>
              {k.issuedByMe && (
                <button type="button" className="btn btn--sm btn--ghost" onClick={() => revoke.mutate(k.id)} disabled={revoke.isPending}>
                  {t('remote.keys.revoke')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        className="row row--tight row--wrap"
        onSubmit={(event) => {
          event.preventDefault()
          issue.mutate()
        }}
      >
        <input
          className="input my-keys__machine"
          value={machine}
          onChange={(event) => setMachine(event.target.value)}
          placeholder={t('remote.keys.machine')}
          aria-label={t('remote.keys.machine')}
          maxLength={100}
        />
        <button type="submit" className="btn btn--sm btn--primary" disabled={issue.isPending}>
          <KeyRound size={13} aria-hidden /> {t('remote.keys.issue')}
        </button>
      </form>
      {failure && (
        <span className="field__error">
          {failure instanceof ApiError && failure.code === 'not_a_developer' ? t('remote.noDeveloper') : t('error.generic')}
        </span>
      )}

      {/* Is the key my agent uses still good? (docs/refactor/26: the key in the editor had been revoked, unseen.) */}
      <KeyCheck />
    </div>
  )
}
